// import i18nFactory from '@yandex-int/i18n';
import { Button } from 'lego-on-react';
import * as React from 'react';
import simplify from 'simplify-js';

import { Bem } from '../../service/Bem/Bem';

import './MapPolygonsDrawer.css';
// import * as keyset from './MapPolygonsDrawer.i18n';

// const i18n = i18nFactory(keyset);

export interface MapPolygonsDrawerProps {
    map: ymaps.Map;
}

export class MapPolygonsDrawer extends React.PureComponent<
    MapPolygonsDrawerProps
> {
    private line: ymaps.Polyline;
    private activePolygon: ymaps.Polygon;
    private polygonsGeoCollection: ymaps.GeoObjectCollection;
    private isEnabled: boolean;
    private isMouseDown: boolean;
    private linePoints: number;
    private COORDS_PRECISION = 0.0000001;

    constructor(props: MapPolygonsDrawerProps) {
        super(props);

        this.polygonsGeoCollection = new ymaps.GeoObjectCollection();
        this.isEnabled = false;

        this.init();
    }

    render(): React.ReactNode {
        return (
            <div className={Bem.cls('map-polygon-drawer')}>
                <Button
                    theme="normal"
                    size="head"
                    type="check"
                    pin="round-round"
                    text="Draw"
                    onClick={() => {
                        this.toggleDraw(!this.isEnabled);
                    }}
                />
                <Button
                    theme="normal"
                    size="head"
                    type="submit"
                    pin="round-round"
                    text="Finish"
                    disabled={true}
                />
                <Button
                    theme="normal"
                    size="head"
                    type="submit"
                    pin="round-round"
                    text="Clear"
                    onClick={() => {
                        this.clearAll();
                    }}
                />
            </div>
        );
    }

    init = () => {
        this.props.map.geoObjects.add(this.polygonsGeoCollection);
    };

    toggleDraw = (isEnabled: boolean) => {
        if (isEnabled === this.isEnabled) {
            return;
        }

        this.polygonsGeoCollection.each(geoObject =>
            this.togglePolygon(geoObject, isEnabled)
        );

        const method = isEnabled ? 'add' : 'remove';
        const { map } = this.props;

        if (map) {
            map.events[method]('mouseup', this.handlePointerUp)
                [method]('mousedown', this.handlePointerDown)
                [method]('mousemove', this.handlePointerMove)
                [method]('mouseleave', this.handlePointerLeave);

            map.behaviors[isEnabled ? 'disable' : 'enable']([
                'drag',
                'rightMouseButtonMagnifier'
            ]);
        }

        this.setActivePolygon(null);
        this.isEnabled = isEnabled;
    };

    drawStart = (vertex: number[]) => {
        this.clear();
        this.isMouseDown = true;

        this.draw(vertex);
    };

    draw = (nextVertex: number[]) => {
        const line = this.line;
        const lastVertex =
            line &&
            this.linePoints > 0 &&
            line.geometry.get(this.linePoints - 1);

        if (
            lastVertex &&
            ymaps.util.math.areEqual(
                nextVertex,
                lastVertex,
                this.COORDS_PRECISION
            )
        ) {
            return;
        }

        this.linePoints++;

        if (line) {
            line.geometry.insert(this.linePoints, nextVertex);
        } else {
            this.addPolyline([nextVertex]);
        }
    };

    drawEnd = () => {
        const { geometry } = this.line;
        const firstVertex = geometry.get(0);
        const lastVertex = geometry.get(geometry.getLength() - 1);
        const path = this.getLinePath();

        if (
            firstVertex &&
            lastVertex &&
            !ymaps.util.math.areEqual(
                firstVertex,
                lastVertex,
                this.COORDS_PRECISION
            )
        ) {
            this.draw(firstVertex);
        }

        if (path && path.length >= 3) {
            this.addPolygon(path);
        }

        this.isMouseDown = false;
        this.clear();
    };

    handlePointerDown = (e: ymaps.Event) => {
        const coords = e.get('coords');

        if (this.getHoveredPolygon()) {
            return;
        }

        this.setActivePolygon(null);
        this.drawStart(coords);
    };

    clear = () => {
        if (this.line) {
            this.line.geometry.setCoordinates([]);
        }

        this.linePoints = 0;
    };

    clearAll = () => this.polygonsGeoCollection.removeAll();

    togglePolygon = (polygon: ymaps.Polygon, isEnabled: boolean) => {
        polygon.options
            .set('cursor', isEnabled ? 'pointer' : 'drag')
            .set('zIndex', isEnabled ? 1 : 0)
            .set(
                'interactivityModel',
                isEnabled ? 'default#geoObject' : 'default#silent'
            );

        polygon.geometry[isEnabled ? 'unfreeze' : 'freeze']();

        polygon.events[isEnabled ? 'add' : 'remove'](
            'click',
            this.handlePolygonClick
        );
    };

    setActivePolygon = (polygon: ymaps.Polygon) => {
        if (this.activePolygon) {
            this.activePolygon.editor.stopEditing();
        }

        if (polygon) {
            this.activePolygon = polygon;
            polygon.editor.startEditing();
        }
    };

    getHoveredPolygon = () => {
        return this.polygonsGeoCollection
            .toArray()
            .find(polygon => polygon.state.get('hover'));
    };

    getLinePath = () => {
        let path = this.simplifyPath(this.line.geometry.getCoordinates());
        let tries = 0;

        while (path.length > 50) {
            path = this.simplifyPath(path);
            tries++;

            if (tries > 5) {
                return null;
            }
        }

        return path;
    };

    handlePolygonClick = (e: ymaps.Event) => {
        this.setActivePolygon(e.get('target'));
    };

    handlePointerMove = (e: ymaps.Event) => {
        if (!this.isMouseDown) {
            return;
        }

        const coords = e.get('coords');
        const sourceEvent = e.getSourceEvent();

        if (sourceEvent) {
            sourceEvent.preventDefault();
            sourceEvent.stopPropagation();
            sourceEvent.stopImmediatePropagation();
        }

        this.draw(coords);

        if (this.getHoveredPolygon()) {
            this.drawEnd();
        }
    };

    handlePointerLeave = (e: ymaps.Event) => {
        if (!this.isMouseDown) {
            return;
        }

        const relatedTarget = e.get('relatedTarget');
        const shape =
            relatedTarget && relatedTarget.getShape && relatedTarget.getShape();

        // ignore crossing the line
        if (
            shape &&
            shape.equals(
                this.line._geoObjectComponent.getOverlaySync().getShape()
            )
        ) {
            return;
        }

        this.drawEnd();
    };

    handlePointerUp = () => {
        if (!this.isMouseDown) {
            return;
        }

        this.drawEnd();
    };

    addPolyline = (path: number[][]) => {
        const line = (this.line = new ymaps.Polyline(
            path,
            {},
            {
                strokeWidth: 3,
                strokeColor: '#0000FF',
                cursor: 'arrow',
                hasHint: false,
                hasBalloon: false,
                openHintOnHover: false,
                interactivityModel: 'default#silent',
                zIndex: 1000
            }
        ));

        this.props.map.geoObjects.add(line);

        return line;
    };

    addPolygon = (coords = []) => {
        const myGeoObject = new ymaps.GeoObject(
            {
                geometry: {
                    type: 'Polygon',
                    coordinates: [coords]
                }
            },
            {
                opacity: 0.5,
                fillColor: '#6699ff',
                strokeColor: '#164900',
                strokeWidth: 3,
                hasHint: false,
                hasBalloon: false,
                editorMinPoints: 4,
                editorMaxPoints: 50,
                editorAddInterior: false,
                editorAddInteriors: false,
                pane: 'areas'
            }
        );

        this.polygonsGeoCollection.add(myGeoObject);
        this.togglePolygon(myGeoObject, true);
        this.setActivePolygon(myGeoObject);
    };

    simplifyPath = (path: []) => {
        const length = path.length;

        if (length <= 3) {
            return path;
        }

        const simplifyTolerance =
            path.reduce(
                (
                    res: {
                        delta: number;
                        prev: number[];
                    },
                    vertex: number[]
                ) => {
                    res.delta +=
                        Math.abs(res.prev[0] - vertex[0]) +
                        Math.abs(res.prev[1] - vertex[1]);
                    res.prev = vertex;

                    return res;
                },
                {
                    prev: path[length - 1],
                    delta: 0
                }
            ).delta /
            length /
            2;

        return simplify(
            path.map((p: string[]) => ({
                x: Number.parseFloat(p[0]),
                y: Number.parseFloat(p[1])
            })),
            simplifyTolerance,
            true
        ).map((p: { x: number; y: number }) => [p.x, p.y]);
    };
}
